import React, { FC, memo, useCallback, useMemo, useRef } from "react";

// components
import { PlanInfoCTA } from "../PlanInfoCTA";
import { OneTapCta } from "../OneTapCta";
import TitleWrapper from "./TitleWrapper";
import { ChangePlanCta } from "../ChangePlanCta";

// dls
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";

// styles
import {
    Container,
    CtaAndPriceContainer,
    PriceContainer,
    TextContainer,
    BasePrice,
    BorderLine,
    BlackBackgroundImage,
    NudgeWrapper,
} from "./styles";

// constants
import {
    BLACK_MEMBERSHIP_BACKGROUND_ALT,
    BLACK_MEMBERSHIP_BACKGROUND_ASSET,
    CART_UPSELL_TEST_IDS,
    TITLE_UNDERLINE_DEFAULT_WIDTH,
    VARIANTS,
} from "../../constants";

// helpers
import { checkCtaType, priceParser, textParser, validateCtas, findCartUpsellCTAs } from "../../helpers";
import { isEmpty } from "@swiggy-private/js-utils";

// hooks
import { isMWebPlatform, useEffectOnce } from "@swiggy-private/core-ui";

// types
import { CartUpsellNudgeAnalytics, OneMembershipHandlers } from "@super-ui/components/cart-upsell-nudge/types";
import { IRemovePlanMessage, PlanDetails, TGCTAs, TGLogoCTX } from "@swiggy-private/cart-types";
import { CTATypes } from "@super-ui/components/swiggy-one-widget-dls-wrapper";
import { NEWRELIC_TABLES, recordNREvent, SwgyHeaderKeysList, GlobalHeaderState } from "@swiggy-private/checkout-core";

interface CartUpsellNudgeProps {
    title: string;
    logoCtx: TGLogoCTX;
    ctas: TGCTAs[];
    note: string;
    attributeValues: Record<string, string>;
    planDetails: PlanDetails;
    handlers: OneMembershipHandlers;
    setShowRemovePlanConfirmation: (value: boolean) => void;
    nudgeAnalytics: CartUpsellNudgeAnalytics;
    isCartV2?: boolean;
    isBlackPlan: boolean;
    removePlanMessage: IRemovePlanMessage;
}

const CartUpsellNudge: FC<CartUpsellNudgeProps> = ({
    title,
    planDetails,
    note,
    ctas,
    attributeValues,
    logoCtx,
    handlers,
    nudgeAnalytics,
    setShowRemovePlanConfirmation,
    isCartV2,
    isBlackPlan = false,
    removePlanMessage,
}) => {
    const { navigateToDeeplinkGenericHelper, addSuperToCart, removeSuperFromCart } = handlers;
    // Using this ref for border line width calculation
    const titleRef = useRef<HTMLDivElement>(null);
    const platform = (GlobalHeaderState.getHeader(SwgyHeaderKeysList.platform) || "").toLowerCase();
    // Parse title and prices
    const { variant, text } = useMemo(() => textParser(title), [title]);

    const isV1Variant = useMemo(() => variant === (VARIANTS.V1 as typeof VARIANTS[keyof typeof VARIANTS]), [variant]);

    const { basePrice, newPrice } = useMemo(() => priceParser(note), [note]);

    const { viewAllBenefits, changePlanCta, oneTapCta } = useMemo(() => findCartUpsellCTAs(ctas), [ctas]);

    const planInCart = useMemo(() => {
        if (checkCtaType(oneTapCta?.type, CTATypes.ONE_TAP_REMOVE)) {
            return String(planDetails?.plan_id);
        }
        return "";
    }, [oneTapCta?.type, planDetails.plan_id]);

    const {
        onImpressionOneMembershipNudge,
        onClickViewAllBenefits,
        onClickAddPlanCta,
        onClickRemovePlanCta,
        onClickChangePlanCta,
        onImpressionChangePlanCta,
    } = nudgeAnalytics;

    useEffectOnce(() => onImpressionOneMembershipNudge());

    // Handlers
    const handleOneTapClick = useCallback(() => {
        if (checkCtaType(oneTapCta?.type)) {
            addSuperToCart(String(planDetails?.plan_id || ""), planDetails?.type);
            onClickAddPlanCta();
        } else if (checkCtaType(oneTapCta?.type, CTATypes.ONE_TAP_REMOVE)) {
            onClickRemovePlanCta();
            if (!isEmpty(removePlanMessage)) {
                setShowRemovePlanConfirmation(true);
            } else {
                removeSuperFromCart(oneTapCta?.text || "");
                recordNREvent({
                    eventType: NEWRELIC_TABLES.EMPTY_REMOVE_PLAN_MESSAGE,
                });
            }
        }
    }, [
        addSuperToCart,
        onClickAddPlanCta,
        onClickRemovePlanCta,
        oneTapCta?.type,
        planDetails?.plan_id,
        planDetails?.type,
        setShowRemovePlanConfirmation,
        removePlanMessage,
        removeSuperFromCart,
        oneTapCta?.text,
    ]);

    const handlePlanInfoCTA = useCallback(() => {
        if (!isEmpty(viewAllBenefits) && !isEmpty(viewAllBenefits.link)) {
            navigateToDeeplinkGenericHelper(viewAllBenefits.link);
            onClickViewAllBenefits();
        }
    }, [navigateToDeeplinkGenericHelper, onClickViewAllBenefits, viewAllBenefits]);

    const areCtasValid = useMemo(() => validateCtas(ctas), [ctas]);
    const isWeb = useMemo(() => isEmpty(viewAllBenefits) && isMWebPlatform(platform), [viewAllBenefits, platform]);

    if (!areCtasValid) {
        return null;
    }

    return (
        <NudgeWrapper>
            <Container isWeb={isWeb} data-testid={CART_UPSELL_TEST_IDS.NUDGE} isBlackPlan={isBlackPlan}>
                {isBlackPlan && (
                    <BlackBackgroundImage>
                        <Image cloudinaryId={BLACK_MEMBERSHIP_BACKGROUND_ASSET} alt={BLACK_MEMBERSHIP_BACKGROUND_ALT} />
                    </BlackBackgroundImage>
                )}
                <TextContainer isWeb={isWeb} data-testid={CART_UPSELL_TEST_IDS.TITLE} isBlackPlan={isBlackPlan}>
                    <TitleWrapper
                        variant={variant}
                        text={text}
                        logoCtx={logoCtx}
                        planDetails={planDetails}
                        titleRef={titleRef}
                    />
                    {isV1Variant && (
                        <BorderLine width={titleRef?.current?.offsetWidth || TITLE_UNDERLINE_DEFAULT_WIDTH} />
                    )}
                </TextContainer>

                <CtaAndPriceContainer isWeb={isWeb} isV1Variant={isV1Variant}>
                    {!isEmpty(viewAllBenefits) && (
                        <PlanInfoCTA
                            text={viewAllBenefits?.text}
                            handler={handlePlanInfoCTA}
                            variant={variant}
                            attributeValues={attributeValues}
                        />
                    )}

                    {!isEmpty(oneTapCta) && (
                        <OneTapCta
                            {...oneTapCta}
                            handleOneTapClick={handleOneTapClick}
                            isCartV2={isCartV2}
                            isBlackPlan={isBlackPlan}
                        />
                    )}

                    {!isEmpty(newPrice) && (
                        <PriceContainer isCartV2={isCartV2}>
                            {!isEmpty(basePrice) && (
                                <BasePrice
                                    isBlackPlan={isBlackPlan}
                                    variant="V2.caption.neutral.medium"
                                    color="text_Med_Emphasis"
                                    dangerouslySetInnerHTML={{ __html: basePrice }}
                                    textAlign="right"
                                />
                            )}

                            <Text
                                variant={isCartV2 ? "V2.body2.neutral.regular" : "V2.body2.neutral.medium"}
                                color="text_High_Emphasis"
                                dangerouslySetInnerHTML={{ __html: newPrice }}
                                textAlign="right"
                            />
                        </PriceContainer>
                    )}
                </CtaAndPriceContainer>
                {!isEmpty(changePlanCta) && (
                    <ChangePlanCta
                        {...changePlanCta}
                        isBlackPlan={isBlackPlan}
                        handlers={handlers}
                        planId={planDetails?.plan_id}
                        planInCart={planInCart}
                        onImpressionChangePlanCta={onImpressionChangePlanCta}
                        onClickChangePlanCta={onClickChangePlanCta}
                    />
                )}
            </Container>
        </NudgeWrapper>
    );
};

export default memo(CartUpsellNudge);
